import { map, catchError, Observable, throwError } from 'rxjs';

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ToastService } from '@/app/shared/components/ui/toast';

import {
  HttpGetConfig,
  HttpPutConfig,
  EntityResponse,
  HttpPostConfig,
  BaseHttpConfig,
  PayloadResponse,
  HttpDeleteConfig,
  PaginationResponse,
  DeleteEntityResponse,
  EntityListWithPaginationResponse,
} from './http.types';

import { environment } from '@/environments/environment';
import { EnvironmentInterface } from '@/environments/interfaces/environment.interface';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  readonly baseUrl: string;

  constructor(
    private httpClient: HttpClient,
    private toastService: ToastService,
  ) {
    this.baseUrl = (environment as EnvironmentInterface).apiUrl;
  }

  private _handleError(error: any): Observable<any> {
    this.toastService.httpError(error);
    return throwError(() => error);
  }

  private _handleResponse<T>(response: Observable<T>, config?: BaseHttpConfig) {
    return response.pipe(
      catchError((error) => {
        if (config?.skipHandleError) {
          return throwError(() => error);
        }

        return this._handleError(error);
      }),
    );
  }

  private _getEndpointUrl(path: string) {
    return `${this.baseUrl}/${path}`;
  }

  get<T>(path: string, config?: HttpGetConfig): Observable<T> {
    return this.httpClient.get<EntityResponse<T>>(this._getEndpointUrl(path), config).pipe(
      (res) => this._handleResponse(res, config),
      map((res) => res.payload.content),
    );
  }

  getAll<T>(path: string, config?: HttpGetConfig): Observable<T[]> {
    return this.httpClient.get<EntityResponse<T[]>>(this._getEndpointUrl(path), config).pipe(
      (res) => this._handleResponse(res, config),
      map((res) => res.payload.content),
    );
  }

  getAllWithPagination<T>(path: string, config?: HttpGetConfig): Observable<PayloadResponse<T[], PaginationResponse>> {
    return this.httpClient.get<EntityListWithPaginationResponse<T>>(this._getEndpointUrl(path), config).pipe(
      (res) => this._handleResponse(res, config),
      map((res) => res.payload),
    );
  }

  post<T, P = unknown>(path: string, body: P | null, config?: HttpPostConfig): Observable<T> {
    return this.httpClient.post<EntityResponse<T>>(this._getEndpointUrl(path), body, config).pipe(
      (res) => this._handleResponse(res, config),
      map((res) => res.payload.content),
    );
  }

  put<T, P = unknown>(path: string, body: P, config?: HttpPutConfig): Observable<T> {
    return this.httpClient.put<EntityResponse<T>>(this._getEndpointUrl(path), body, config).pipe(
      (res) => this._handleResponse(res, config),
      map((res) => res.payload.content),
    );
  }

  delete<T = null>(path: string, config?: HttpDeleteConfig): Observable<T> {
    return this.httpClient.delete<DeleteEntityResponse>(this._getEndpointUrl(path), config).pipe(
      (res) => this._handleResponse(res, config),
      map((res) => res.payload.content),
    );
  }

  downloadFile(
    method: string,
    path: string,
    config?: HttpGetConfig | HttpPostConfig | HttpPutConfig | HttpDeleteConfig,
  ) {
    return this.httpClient
      .request(method, this._getEndpointUrl(path), {
        observe: 'response',
        responseType: 'blob' as 'json',
        ...config,
      })
      .pipe(
        (response) => this._handleResponse(response, config),

        map((response) => {
          return {
            blob: response.body,
            contentDisposition: response.headers?.get('Content-Disposition'),
            contentType: response.headers.get('Content-Type'),
          };
        }),
      );
  }
}
